import {
  computed,
  inject,
  hasInjectionContext,
  onScopeDispose,
  provide,
  readonly,
  ref,
  shallowRef,
  toValue,
  watch
} from "vue";
import { useObservability } from "./useObservability.js";
import {
  useDocumentVisibility,
  useEventListener,
  useOnline
} from "@vueuse/core";
import { LoroDoc, VersionVector } from "loro-crdt";
import { AgentClient } from "agents/client";
import { kebabCase } from "scule";
import { ATTACHMENTS_CONTAINER_ID } from "@rialtic/agent-kit/attachments";
import { b64, unb64 } from "@rialtic/agent-kit/base64";
import { Comments } from "@rialtic/agent-kit/comments";
import { EventBus } from "@rialtic/agent-kit/events";
import { History } from "@rialtic/agent-kit/history";
import { Undo } from "@rialtic/agent-kit/undo";
import { Presence } from "@rialtic/agent-kit/presence";
import {
  TAG_DOC,
  TAG_PRESENCE,
  decodeBinaryFrame,
  decodeControl,
  encodeBinaryFrame,
  encodeControl
} from "@rialtic/agent-kit/protocol";
import { Suggestions } from "@rialtic/agent-kit/suggestions";
import { createIndexedDbPersistence } from "../persistence/indexed-db.js";
const collaborationAgentKey = Symbol("CollaborationAgent");
const resolveQuery = (query) => {
  if (!query) return void 0;
  if (typeof query === "function") return query;
  const val = toValue(query);
  if (!val) return void 0;
  return Object.fromEntries(
    Object.entries(val).filter(([, v]) => v !== void 0 && v !== null)
  );
};
export const provideCollaborationAgent = (client) => {
  if (hasInjectionContext()) {
    provide(
      collaborationAgentKey,
      client
    );
  }
  return client;
};
export const useCollaborationAgent = (options) => {
  const obs = useObservability();
  const status = ref("idle");
  const online = useOnline();
  const error = ref(null);
  const denied = ref(null);
  const state = shallowRef();
  const identity = ref({
    agent: toValue(options.agent),
    name: toValue(options.name) ?? "default",
    identified: false
  });
  const documentVersion = ref(0);
  const presenceVersion = ref(0);
  const textContainer = toValue(options.textContainer) ?? "body";
  const peerId = toValue(options.peerId) ?? globalThis.crypto?.randomUUID?.() ?? `peer-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const doc = new LoroDoc();
  const events = new EventBus();
  const presenceMgr = new Presence({
    peerId,
    broadcast: (payload) => sendBinary(TAG_PRESENCE, payload)
  });
  const commentsMgr = new Comments({ doc, textContainer, events });
  const suggestionsMgr = new Suggestions({ doc, textContainer, events });
  const historyMgr = new History({ doc });
  const undoMgr = new Undo({ doc });
  let importing = false;
  let uploadSeq = 0;
  const persistence = options.persistence === false ? void 0 : options.persistence ?? (typeof indexedDB === "undefined" ? void 0 : createIndexedDbPersistence(
    `${identity.value.agent}/${toValue(options.name) ?? "default"}/${textContainer}`
  ));
  let saveTimer;
  let unsavedChanges = false;
  let saveSeq = 0;
  const persistenceError = ref(null);
  const writeSnapshot = (context) => {
    if (!persistence || !unsavedChanges) return;
    unsavedChanges = false;
    const seq = ++saveSeq;
    void persistence.save(doc.export({ mode: "snapshot" })).then(() => {
      if (seq === saveSeq) persistenceError.value = null;
    }).catch((e) => {
      unsavedChanges = true;
      if (seq === saveSeq) {
        persistenceError.value = e instanceof Error ? e.message : "Could not save a local copy";
      }
      obs.error(e, { function: `useCollaborationAgent:${context}` });
    });
  };
  const schedulePersist = () => {
    if (!persistence) return;
    unsavedChanges = true;
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      saveTimer = void 0;
      writeSnapshot("schedulePersist");
    }, 800);
  };
  const flushPersist = (context) => {
    clearTimeout(saveTimer);
    saveTimer = void 0;
    writeSnapshot(context);
  };
  watch(useDocumentVisibility(), (visibility) => {
    if (visibility === "hidden") flushPersist("visibilityHidden");
  });
  useEventListener("pagehide", () => flushPersist("pageHide"));
  const unsubscribeDoc = doc.subscribe(() => {
    documentVersion.value++;
    schedulePersist();
  });
  const refreshPresence = () => {
    presenceVersion.value++;
  };
  let localPresence = {};
  let uploadingPresence;
  const pushPresence = () => {
    presenceMgr.setLocal(
      uploadingPresence ? { ...localPresence, uploading: uploadingPresence } : localPresence
    );
    refreshPresence();
  };
  const setUploadingPresence = (u) => {
    uploadingPresence = u;
    pushPresence();
  };
  const startClosed = toValue(options.autoConnect) === false || typeof WebSocket === "undefined" && !options.WebSocket;
  const agentPath = () => toValue(options.path) ?? kebabCase(toValue(options.agent));
  const normalizedBasePath = () => {
    const basePath = toValue(options.basePath);
    if (!basePath) return void 0;
    const normalized = basePath.replace(/^\/+/, "").replace(/\/+$/, "");
    return normalized || void 0;
  };
  const wsBasePath = () => {
    const basePath = normalizedBasePath();
    if (basePath === void 0) return void 0;
    const room = toValue(options.name) ?? "default";
    return `${basePath}/${agentPath()}/${encodeURIComponent(room)}`;
  };
  const connectionOptions = {
    agent: toValue(options.agent),
    name: toValue(options.name),
    host: toValue(options.host) ?? globalThis.location?.origin,
    basePath: wsBasePath(),
    /*
     * `wsBasePath()` already embeds `path` as the agent segment, and
     * partysocket appends `path` again after `basePath` — so forwarding both
     * yields `…/<path>/<room>/<path>` and a socket URL that no longer matches
     * `restBase()`. Only forward it when there is no basePath to fold it into.
     */
    path: toValue(options.basePath) ? void 0 : toValue(options.path),
    query: resolveQuery(options.query),
    startClosed,
    WebSocket: options.WebSocket,
    onStateUpdate: (s, source) => {
      state.value = s;
      options.onStateUpdate?.(s, source);
    },
    onStateUpdateError: (e) => {
      error.value = e;
      options.onStateUpdateError?.(e);
    },
    onIdentity: (name, agent) => {
      identity.value = { name, agent, identified: true };
      options.onIdentity?.(name, agent);
    },
    onIdentityChange: options.onIdentityChange
  };
  const connection = new AgentClient(connectionOptions);
  connection.binaryType = "arraybuffer";
  if (!startClosed) status.value = "connecting";
  const send = (data) => {
    if (connection.readyState !== 1) return false;
    connection.send(data);
    return true;
  };
  const sendBinary = (tag, payload) => {
    send(encodeBinaryFrame(tag, payload));
  };
  const sendControl = (message) => send(encodeControl(message));
  const sync = () => sendControl({ c: "sync", vv: b64(doc.version().encode()) });
  const unsubscribeDocUpdates = doc.subscribeLocalUpdates((payload) => {
    if (!importing) sendBinary(TAG_DOC, payload);
  });
  const applyDocUpdate = (payload) => {
    importing = true;
    try {
      doc.import(payload);
    } finally {
      importing = false;
    }
  };
  const handleBinaryFrame = (bytes) => {
    try {
      const frame = decodeBinaryFrame(bytes);
      if (frame.tag === TAG_DOC) {
        applyDocUpdate(frame.payload);
      } else {
        presenceMgr.apply(frame.payload);
        refreshPresence();
      }
    } catch (e) {
      obs.error(e, {
        function: "useCollaborationAgent:handleBinaryFrame"
      });
    }
  };
  connection.addEventListener("open", (event) => {
    status.value = "open";
    error.value = null;
    denied.value = null;
    sync();
    options.onOpen?.(event);
  });
  connection.addEventListener("close", (event) => {
    status.value = "closed";
    identity.value = { ...identity.value, identified: false };
    options.onClose?.(event);
  });
  connection.addEventListener("error", (event) => {
    status.value = "error";
    error.value = "WebSocket error";
    options.onError?.(event);
  });
  connection.addEventListener("message", (event) => {
    const msgEvent = event;
    if (msgEvent.data instanceof ArrayBuffer) {
      handleBinaryFrame(new Uint8Array(msgEvent.data));
    } else if (msgEvent.data instanceof Uint8Array) {
      handleBinaryFrame(msgEvent.data);
    } else if (typeof msgEvent.data === "string") {
      let msg;
      try {
        msg = decodeControl(msgEvent.data);
      } catch {
        return;
      }
      if (msg.c === "sync-reply") {
        try {
          const serverVv = VersionVector.decode(unb64(msg.vv));
          const relation = doc.version().compare(serverVv);
          if (relation === 1 || relation === void 0) {
            sendBinary(TAG_DOC, doc.export({ mode: "update", from: serverVv }));
          }
        } catch (e) {
          obs.error(e, { function: "useCollaborationAgent:syncReply" });
        }
      } else if (msg.c === "denied") {
        denied.value = msg.reason;
        obs.event("collab:denied", { reason: msg.reason });
      }
    }
  });
  const hydrate = async () => {
    if (!persistence) return;
    let snapshot;
    try {
      snapshot = await persistence.load();
    } catch (e) {
      obs.error(e, { function: "useCollaborationAgent:hydrate" });
      return;
    }
    if (!snapshot) return;
    try {
      applyDocUpdate(snapshot);
    } catch (e) {
      obs.error(e, { function: "useCollaborationAgent:hydrate" });
      return;
    }
    if (status.value === "open") sync();
  };
  const connect = () => {
    if (status.value === "open" || status.value === "connecting") return;
    status.value = "connecting";
    connection.reconnect();
  };
  const disconnect = () => {
    connection.close();
    status.value = "closed";
  };
  const restBase = () => {
    const host = (toValue(options.host) ?? globalThis.location?.origin ?? "").replace(/^ws(s?):\/\//, "http$1://");
    const basePath = normalizedBasePath() ?? "agents";
    const room = toValue(options.name) ?? "default";
    return `${host}/${basePath}/${agentPath()}/${encodeURIComponent(room)}`;
  };
  const authedFetch = async (url, init) => {
    const token = await resolveToken();
    return fetch(url, {
      ...init,
      headers: {
        ...init?.headers,
        ...token ? { Authorization: `Bearer ${token}` } : {}
      }
    });
  };
  const resolveToken = async () => {
    const q = options.query;
    if (!q) return void 0;
    if (typeof q === "function") {
      const r = await q();
      return r?.token;
    }
    const val = toValue(q);
    return val?.token;
  };
  const uploadQueue = ref([]);
  const taskFiles = /* @__PURE__ */ new Map();
  const activeUploads = /* @__PURE__ */ new Set();
  const filesList = computed(() => {
    void documentVersion.value;
    return doc.getMovableList(ATTACHMENTS_CONTAINER_ID).toArray();
  });
  const putFile = (task, file) => new Promise((resolve, reject) => {
    void (async () => {
      const token = await resolveToken();
      const url = new URL(`${restBase()}/upload`);
      url.searchParams.set("path", file.name);
      url.searchParams.set(
        "contentType",
        file.type || "application/octet-stream"
      );
      url.searchParams.set("uploadId", task.id);
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url.toString());
      xhr.timeout = 6e4;
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      activeUploads.add(xhr);
      const settle = (run) => {
        activeUploads.delete(xhr);
        run();
      };
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        task.progress = e.loaded / e.total;
        setUploadingPresence({ name: file.name, progress: task.progress });
      };
      xhr.onload = () => settle(() => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch {
            reject(new Error("invalid upload response"));
          }
        } else {
          reject(new Error(`upload failed (${xhr.status})`));
        }
      });
      xhr.onerror = () => settle(() => reject(new Error("network error")));
      xhr.ontimeout = () => settle(() => reject(new Error("upload timed out")));
      xhr.onabort = () => settle(() => reject(new Error("upload aborted")));
      xhr.send(file);
    })().catch(reject);
  });
  const runUpload = async (task) => {
    const file = taskFiles.get(task.id);
    if (!file) return void 0;
    const maxAttempts = 3;
    while (task.attempts < maxAttempts) {
      task.attempts++;
      task.status = "uploading";
      task.error = void 0;
      try {
        const meta = await putFile(task, file);
        task.status = "done";
        task.progress = 1;
        setUploadingPresence(void 0);
        taskFiles.delete(task.id);
        return meta;
      } catch (e) {
        task.error = e instanceof Error ? e.message : "upload failed";
        if (task.attempts >= maxAttempts) {
          task.status = "error";
          setUploadingPresence(void 0);
          return void 0;
        }
        await new Promise((r) => setTimeout(r, 500 * 2 ** (task.attempts - 1)));
      }
    }
    return void 0;
  };
  const upload = async (file) => {
    const id = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${uploadSeq++}`;
    taskFiles.set(id, file);
    uploadQueue.value = [
      ...uploadQueue.value,
      {
        id,
        name: file.name,
        size: file.size,
        status: "queued",
        progress: 0,
        attempts: 0
      }
    ];
    const task = uploadQueue.value[uploadQueue.value.length - 1];
    return runUpload(task);
  };
  const retry = async (taskId) => {
    const task = uploadQueue.value.find((t) => t.id === taskId);
    if (!task) return void 0;
    task.attempts = 0;
    return runUpload(task);
  };
  const remove = async (path) => {
    const url = new URL(`${restBase()}/file`);
    url.searchParams.set("path", path);
    const res = await authedFetch(url.toString(), { method: "DELETE" });
    if (!res.ok) throw new Error(`delete failed (${res.status})`);
  };
  const downloadUrl = async (path) => {
    const url = new URL(`${restBase()}/file`);
    url.searchParams.set("path", path);
    const res = await authedFetch(url.toString());
    if (!res.ok) throw new Error(`download failed (${res.status})`);
    return URL.createObjectURL(await res.blob());
  };
  const readText = async (path) => {
    const url = new URL(`${restBase()}/file`);
    url.searchParams.set("path", path);
    const res = await authedFetch(url.toString());
    if (!res.ok) throw new Error(`download failed (${res.status})`);
    return res.text();
  };
  const docClient = {
    doc,
    text: computed(() => {
      void documentVersion.value;
      return doc.getText(textContainer);
    }),
    content: computed(() => {
      void documentVersion.value;
      return doc.getText(textContainer).toString();
    }),
    version: readonly(documentVersion),
    sync
  };
  const client = {
    connection,
    status: readonly(status),
    online: readonly(online),
    error: readonly(error),
    denied: readonly(denied),
    persistenceError: readonly(persistenceError),
    state: readonly(state),
    identity: readonly(identity),
    document: docClient,
    text: docClient.content,
    events,
    presence: {
      peers: computed(() => {
        void presenceVersion.value;
        return presenceMgr.all();
      }),
      setLocal: (s) => {
        localPresence = s;
        pushPresence();
      }
    },
    comments: {
      list: computed(() => {
        void documentVersion.value;
        return commentsMgr.list();
      }),
      add: (input) => sendControl({
        c: "comment",
        op: "parentId" in input ? "reply" : "add",
        data: input
      }),
      resolve: (id) => sendControl({ c: "comment", op: "resolve", data: id }),
      reopen: (id) => sendControl({ c: "comment", op: "reopen", data: id })
    },
    suggestions: {
      list: computed(() => {
        void documentVersion.value;
        return suggestionsMgr.list();
      }),
      feedbackSummary: computed(() => {
        void documentVersion.value;
        return suggestionsMgr.feedbackSummary();
      }),
      propose: (input) => suggestionsMgr.propose(input),
      /**
       * Send an accept control frame. Returns `true` if the frame was sent,
       * `false` when the socket is unavailable — NOT whether the suggestion
       * was actually applied (that comes back over the CRDT doc).
       */
      accept: (id) => sendControl({ c: "suggestion", op: "accept", id }),
      /**
       * Send a reject control frame. Returns `true` if the frame was sent,
       * `false` when the socket is unavailable — NOT whether the suggestion
       * was actually applied (that comes back over the CRDT doc).
       */
      reject: (id, feedback) => sendControl({ c: "suggestion", op: "reject", id, feedback })
    },
    history: {
      versions: computed(() => {
        void documentVersion.value;
        return historyMgr.versions();
      }),
      preview: (frontiers) => historyMgr.preview(frontiers),
      revertTo: (frontiers) => historyMgr.revertTo(frontiers)
    },
    undo: {
      undo: () => undoMgr.undo(),
      redo: () => undoMgr.redo(),
      canUndo: () => undoMgr.canUndo(),
      canRedo: () => undoMgr.canRedo(),
      manager: undoMgr.raw
    },
    milestones: {
      list: computed(() => state.value?.milestones ?? []),
      complete: (id) => sendControl({ c: "milestone", op: "complete", id }),
      reopen: (id) => sendControl({ c: "milestone", op: "reopen", id })
    },
    files: {
      list: filesList,
      queue: readonly(uploadQueue),
      upload,
      retry,
      remove,
      downloadUrl,
      readText
    },
    rest: {
      /* Tolerate both `'file'` and `'/file'` — the template already supplies
         the separator, so a leading slash would double it. */
      url: (subpath) => `${restBase()}/${subpath.replace(/^\/+/, "")}`,
      token: resolveToken
    },
    connect,
    disconnect,
    send,
    sendControl,
    setState: (s) => connection.setState(s)
  };
  provideCollaborationAgent(client);
  void hydrate();
  onScopeDispose(() => {
    disconnect();
    for (const xhr of activeUploads) xhr.abort();
    unsubscribeDocUpdates();
    unsubscribeDoc();
    presenceMgr.dispose();
    flushPersist("dispose");
    doc.free();
  }, true);
  return client;
};
export const resolveCollaborationAgent = (client) => {
  if (client) return client;
  const injected = inject(collaborationAgentKey);
  if (!injected) throw new Error("No CollaborationAgent client was provided");
  return injected;
};
